import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { 
  GLAccount, 
  subscribeToGLAccounts, 
  deleteGLAccount,
  accountTypes
} from '../firebase/glAccountServices';
import GLAccountForm from '../components/GLAccountForm';
import GLAccountHierarchyView from '../components/GLAccountHierarchyView';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`gl-account-tabpanel-${index}`}
      aria-labelledby={`gl-account-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const GLAccounts: React.FC = () => {
  // State for GL accounts data
  const [level1Accounts, setLevel1Accounts] = useState<GLAccount[]>([]);
  const [level2Accounts, setLevel2Accounts] = useState<GLAccount[]>([]);
  const [level3Accounts, setLevel3Accounts] = useState<GLAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for tab navigation
  const [tabValue, setTabValue] = useState(0);
  
  // State for selected accounts
  const [selectedLevel1, setSelectedLevel1] = useState<GLAccount | null>(null);
  const [selectedLevel2, setSelectedLevel2] = useState<GLAccount | null>(null);
  
  // State for menu and dialogs
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [hierarchyViewOpen, setHierarchyViewOpen] = useState(false);
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [editAccountId, setEditAccountId] = useState<string | null>(null);
  const [accountFormLevel, setAccountFormLevel] = useState(1);
  const [accountFormParent, setAccountFormParent] = useState<string | null>(null);
  const [editAccount, setEditAccount] = useState<GLAccount | undefined>(undefined);
  const [accountLevel, setAccountLevel] = useState<number>(1);
  const [parentAccount, setParentAccount] = useState<string | undefined>(undefined);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Fetch GL accounts data
  useEffect(() => {
    setLoading(true);
    
    // Subscribe to level 1 accounts
    const unsubscribeLevel1 = subscribeToGLAccounts(
      (accounts) => {
        setLevel1Accounts(accounts.filter(account => account.level === 1));
        setLoading(false);
      }
    );
    
    // Subscribe to level 2 accounts
    const unsubscribeLevel2 = subscribeToGLAccounts(
      (accounts) => {
        setLevel2Accounts(accounts.filter(account => account.level === 2));
      }
    );
    
    // Subscribe to level 3 accounts
    const unsubscribeLevel3 = subscribeToGLAccounts(
      (accounts) => {
        setLevel3Accounts(accounts.filter(account => account.level === 3));
      }
    );
    
    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeLevel1();
      unsubscribeLevel2();
      unsubscribeLevel3();
    };
  }, []);
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Handle level 1 account selection
  const handleLevel1Select = (account: GLAccount) => {
    setSelectedLevel1(account);
    setSelectedLevel2(null);
    setTabValue(1);
  };
  
  // Handle level 2 account selection
  const handleLevel2Select = (account: GLAccount) => {
    setSelectedLevel2(account);
    setTabValue(2);
  };
  
  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setEditAccountId(id);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setEditAccountId(null);
  };
  
  // Clearing account functions removed - now handled automatically when main accounts are created
  
  // Handle edit account
  const handleEditAccount = (account: GLAccount) => {
    setEditAccount(account);
    setAccountLevel(account.level);
    setParentAccount(account.parentAccount);
    setAccountFormOpen(true);
    handleMenuClose();
  };
  
  // Handle add new account
  const handleAddAccount = (level: number, parentAccountNumber?: string) => {
    setEditAccount(undefined);
    setAccountLevel(level);
    setParentAccount(parentAccountNumber);
    setAccountFormOpen(true);
  };
  
  // Handle account form close
  const handleAccountFormClose = () => {
    setAccountFormOpen(false);
    setEditAccount(undefined);
  };
  
  // Handle account form save
  const handleAccountFormSave = () => {
    enqueueSnackbar(editAccount ? 'Account updated successfully' : 'Account added successfully', { variant: 'success' });
    setAccountFormOpen(false);
  };
  
  // Handle delete account
  const handleDeleteAccount = async (accountId: string) => {
    try {
      await deleteGLAccount(accountId);
      enqueueSnackbar('Account deleted successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    }
    handleMenuClose();
  };
  
  // Filter accounts based on search query
  const filterAccounts = (accounts: GLAccount[]) => {
    if (!searchQuery) return accounts;
    
    return accounts.filter(account => 
      account.accountNumber.includes(searchQuery) ||
      account.accountName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.accountType.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };
  
  // Get filtered accounts for each level
  const filteredLevel1 = filterAccounts(level1Accounts);
  
  const filteredLevel2 = selectedLevel1
    ? filterAccounts(level2Accounts.filter(account => account.parentAccount === selectedLevel1.accountNumber))
    : [];
    
  const filteredLevel3 = selectedLevel2
    ? filterAccounts(level3Accounts.filter(account => account.parentAccount === selectedLevel2.accountNumber))
    : [];
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          GL Accounts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AccountBalanceIcon />}
            onClick={() => setHierarchyViewOpen(true)}
          >
            View Hierarchy
          </Button>
          {/* Clearing account buttons removed - now handled automatically when main accounts are created */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleAddAccount(1)}
          >
            Add Main Account
          </Button>
        </Box>
      </Box>
      
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="GL account hierarchy tabs"
          >
            <Tab label="Main Accounts" />
            <Tab 
              label="Sub Accounts" 
              disabled={!selectedLevel1}
            />
            <Tab 
              label="Detail Accounts" 
              disabled={!selectedLevel2}
            />
          </Tabs>
        </Box>
        
        {/* Level 1 Accounts Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ width: '300px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Balance Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary" component="span">
                        Loading accounts...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredLevel1.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No accounts found. Create your first GL account.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLevel1.map((account) => (
                    <TableRow 
                      key={account.id} 
                      hover 
                      onClick={() => handleLevel1Select(account)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{account.accountNumber}</TableCell>
                      <TableCell>{account.accountName}</TableCell>
                      <TableCell>{account.accountType}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{account.balanceType}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={account.isActive ? 'Active' : 'Inactive'}
                          color={account.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Sub Accounts">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLevel1Select(account);
                            }}
                          >
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, account.id as string)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* Level 2 Accounts Tab */}
        <TabPanel value={tabValue} index={1}>
          {selectedLevel1 && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Main Account: {selectedLevel1.accountNumber} - {selectedLevel1.accountName}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search sub accounts..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddAccount(2, selectedLevel1.accountNumber)}
                  >
                    Add Sub Account
                  </Button>
                </Box>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account Number</TableCell>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Balance Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLevel2.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No sub accounts found. Create your first sub account.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLevel2.map((account) => (
                        <TableRow 
                          key={account.id} 
                          hover 
                          onClick={() => handleLevel2Select(account)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{account.accountNumber}</TableCell>
                          <TableCell>{account.accountName}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{account.balanceType}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={account.isActive ? 'Active' : 'Inactive'}
                              color={account.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Detail Accounts">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLevel2Select(account);
                                }}
                              >
                                <ArrowForwardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuOpen(e, account.id as string)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>
        
        {/* Level 3 Accounts Tab */}
        <TabPanel value={tabValue} index={2}>
          {selectedLevel2 && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Sub Account: {selectedLevel2.accountNumber} - {selectedLevel2.accountName}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search detail accounts..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddAccount(3, selectedLevel2.accountNumber)}
                  >
                    Add Detail Account
                  </Button>
                </Box>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account Number</TableCell>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Balance Type</TableCell>
                      <TableCell>Current Balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLevel3.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No detail accounts found. Create your first detail account.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLevel3.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>{account.accountNumber}</TableCell>
                          <TableCell>{account.accountName}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{account.balanceType}</TableCell>
                          <TableCell>
                            {account.currentBalance !== undefined
                              ? `RM ${account.currentBalance.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}`
                              : 'RM 0.00'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={account.isActive ? 'Active' : 'Inactive'}
                              color={account.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuOpen(e, account.id as string)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>
      </Paper>
      
      {/* Account Form Dialog */}
      <GLAccountForm
        open={accountFormOpen}
        onClose={handleAccountFormClose}
        account={editAccount}
        level={accountLevel}
        parentAccount={parentAccount}
        onSave={handleAccountFormSave}
      />
      
      {/* GL Account Hierarchy View */}
      <GLAccountHierarchyView
        open={hierarchyViewOpen}
        onClose={() => setHierarchyViewOpen(false)}
      />
      
      {/* Action menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const account = [...level1Accounts, ...level2Accounts, ...level3Accounts]
            .find(a => a.id === editAccountId);
          if (account) {
            handleEditAccount(account);
          }
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (editAccountId) {
            handleDeleteAccount(editAccountId);
          }
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GLAccounts;
